import React, { useState } from 'react';
import { useTenantTerm } from '../../hooks/useTenantTerm';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { Download, FileText, Pencil, Trash2, CalendarX } from 'lucide-react';
import { Lease, calculateYearlyIncome, formatInterval } from '../../types/leases';
import { FileAttachment } from '../../types/attachments';
import { RenameAttachmentModal } from '../../Components/RenameAttachmentModal';
import { formatCurrency } from '../../Helpers/FormatUtils';
import { formatFileSize } from '../../Helpers/FileUtils';
import { getContractDownloadUrl } from '../../Helpers/leaseContractUtils';
import { amountPeriods, coTenantEntries, leaseObjectLabel, leaseStatus, leaseStatusLabel, noticeDeadline, todayStockholm } from '../../Helpers/leaseUtils';
import { translate } from '../../types/EnumTranslations';
import { tenantPath } from '../../types/tenants';

interface LeaseCardProps {
    lease: Lease;
    /** Name lookup for the primary tenant and every co-tenant (`useTenants().getTenantName`). */
    getTenantName: (tenantKey: string) => string;
    /** Shown on the tenant page, where leases span buildings. */
    buildingName?: string;
    /** Link target for the building's leases page (tenant page). */
    buildingLink?: string;
    /** The object as shown, e.g. with the unit's stairwell; defaults to leaseObjectLabel. */
    objectLabel?: string;
    /**
     * The tenant whose page shows the card. The object becomes the title and
     * only the other parties are named, since this tenant is on every card.
     */
    viewingTenantKey?: string;
    isAdmin: boolean;
    submitting?: boolean;
    highlighted?: boolean;
    onEdit?: () => void;
    onEnd?: () => void;
    onDelete?: () => Promise<void>;
    /** Changes a contract's display name; throws on failure (shown in the modal). */
    onRenameContract?: (attachmentKey: string, displayName: string | undefined) => Promise<void>;
}

const LeaseStatusBadge: React.FC<{ lease: Lease }> = ({ lease }) => {
    const status = leaseStatus(lease);
    return <span className={`lease-status lease-status--${status}`}>{leaseStatusLabel(status)}</span>;
};

export const LeaseCard: React.FC<LeaseCardProps> = ({
    lease, getTenantName, buildingName, buildingLink, objectLabel, viewingTenantKey, isAdmin, submitting = false, highlighted = false, onEdit, onEnd, onDelete, onRenameContract
}) => {
    const coTenants = coTenantEntries(lease);
    const tenantWord = useTenantTerm().singular.toLowerCase();
    // On a tenant's page: everyone on the lease except that tenant, the primary named "hyresgäst" ("medlem / hyresgäst" in a BRF).
    const otherParties = viewingTenantKey
        ? [
            ...(lease.tenantKey !== viewingTenantKey ? [{ tenantKey: lease.tenantKey, roleLabel: tenantWord }] : []),
            ...coTenants
                .filter(entry => entry.tenantKey !== viewingTenantKey)
                .map(entry => ({ tenantKey: entry.tenantKey, roleLabel: translate(entry.role).toLowerCase() })),
        ]
        : [];
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [renamingContract, setRenamingContract] = useState<FileAttachment | null>(null);
    const [showHistory, setShowHistory] = useState(false);
    const status = leaseStatus(lease);
    const contracts = Object.values(lease.contracts || {});
    const deadline = status === 'ended' ? null : noticeDeadline(lease);
    const deadlinePassed = !!deadline && deadline < todayStockholm();
    const terms: string[] = [];
    if (lease.noticeMonths) terms.push(`Uppsägning ${lease.noticeMonths} mån`);
    if (lease.renewalMonths) terms.push(`Förlängning ${lease.renewalMonths} mån`);
    if (lease.indexClause) terms.push('Indexklausul');
    if (lease.vatRegistered) terms.push('Moms');
    const periods = lease.amountHistory ? amountPeriods(lease) : [];

    const download = async (storagePath: string, fileName: string) => {
        try {
            const url = await getContractDownloadUrl(storagePath);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error('Error downloading contract:', err);
        }
    };

    return (
        <div id={`lease-${lease.key}`} className={`std-box lease-card ${highlighted ? 'lease-card--highlighted' : ''} ${status === 'ended' ? 'lease-card--ended' : ''}`}>
                {confirmDelete ? (
                    <div className="lease-delete-confirm">
                        <span className="text-danger">Ta bort hyresavtalet och dess avtalsfiler? Historiken försvinner.</span>
                        <div className="d-flex gap-2">
                            <Button variant="danger" size="sm" disabled={submitting} onClick={() => { onDelete?.().finally(() => setConfirmDelete(false)); }}>Ta bort</Button>
                            <Button variant="outline-secondary" size="sm" onClick={() => setConfirmDelete(false)}>Avbryt</Button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="lease-card-header">
                            {viewingTenantKey ? (
                                <div className="lease-card-title">
                                    <strong>{objectLabel ?? leaseObjectLabel(lease)}</strong>
                                    {buildingName && (
                                        <span className="lease-card-object">
                                            {buildingLink ? <Link to={buildingLink}>{buildingName}</Link> : buildingName}
                                        </span>
                                    )}
                                    {otherParties.length > 0 && (
                                        <span className="lease-card-co-tenants text-muted">
                                            {otherParties.map((entry, index) => (
                                                <React.Fragment key={entry.tenantKey}>
                                                    {index > 0 && ' · '}
                                                    <Link to={tenantPath(entry.tenantKey)}>{getTenantName(entry.tenantKey)}</Link>
                                                    {' '}<span className="lease-card-role">({entry.roleLabel})</span>
                                                </React.Fragment>
                                            ))}
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <div className="lease-card-title">
                                    <strong><Link to={tenantPath(lease.tenantKey)}>{getTenantName(lease.tenantKey)}</Link></strong>
                                    {coTenants.length > 0 && (
                                        <span className="lease-card-co-tenants text-muted">
                                            {coTenants.map((entry, index) => (
                                                <React.Fragment key={entry.tenantKey}>
                                                    {index > 0 && ' · '}
                                                    <Link to={tenantPath(entry.tenantKey)}>{getTenantName(entry.tenantKey)}</Link>
                                                    {' '}<span className="lease-card-role">({translate(entry.role).toLowerCase()})</span>
                                                </React.Fragment>
                                            ))}
                                        </span>
                                    )}
                                    <span className="lease-card-object">
                                        {objectLabel ?? leaseObjectLabel(lease)}
                                        {buildingName && <> · {buildingLink ? <Link to={buildingLink}>{buildingName}</Link> : buildingName}</>}
                                    </span>
                                </div>
                            )}
                            <div className="lease-card-amount">
                                <div>
                                    <strong>{formatCurrency(lease.amount)}</strong>
                                    <span className="lease-card-interval">{formatInterval(lease.interval)}</span>
                                </div>
                                {!(lease.interval.unit === 'year' && lease.interval.count === 1) && (
                                    <span className="lease-card-yearly">{formatCurrency(calculateYearlyIncome(lease.amount, lease.interval))}/år</span>
                                )}
                            </div>
                        </div>

                        <div className="lease-card-period">
                            <LeaseStatusBadge lease={lease} />
                            <span>{lease.startDate}{lease.endDate ? ` – ${lease.endDate}` : ' – tills vidare'}</span>
                            {deadline && (
                                <span className={`lease-card-deadline ${deadlinePassed ? 'lease-card-deadline--passed' : ''}`} title="Sista dag att säga upp avtalet">
                                    Uppsägning senast {deadline}
                                </span>
                            )}
                        </div>

                        {(terms.length > 0 || periods.length > 1) && (
                            <div className="lease-card-terms text-muted">
                                {terms.join(' · ')}
                                {periods.length > 1 && (
                                    <>
                                        {terms.length > 0 && ' · '}
                                        <button type="button" className="lease-history-toggle" onClick={() => setShowHistory(v => !v)}>
                                            Hyreshistorik ({periods.length} perioder)
                                        </button>
                                    </>
                                )}
                                {showHistory && (
                                    <ul className="lease-history">
                                        {periods.map(period => (
                                            <li key={period.from}>
                                                {period.from}{period.to ? ` – ${period.to}` : ' –'}: {formatCurrency(period.amount)} {formatInterval(lease.interval)}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}

                        {lease.comment && <p className="lease-card-comment text-muted">{lease.comment}</p>}

                        {contracts.length > 0 && (
                            <div className="lease-contracts">
                                {contracts.map(contract => (
                                    <div key={contract.key} className="lease-contract-item">
                                        <button
                                            type="button"
                                            className="lease-contract-download"
                                            onClick={() => download(contract.storagePath, contract.fileName)}
                                            title={`Ladda ner ${contract.fileName}`}
                                        >
                                            <FileText size={14} />
                                            <span className="lease-contract-name">{contract.displayName || contract.fileName}</span>
                                            <span className="lease-contract-size">({formatFileSize(contract.fileSize)})</span>
                                            <Download size={12} />
                                        </button>
                                        {isAdmin && onRenameContract && (
                                            <button
                                                type="button"
                                                className="lease-contract-rename"
                                                title="Byt namn"
                                                aria-label={`Byt namn på ${contract.displayName || contract.fileName}`}
                                                disabled={submitting}
                                                onClick={() => setRenamingContract(contract)}
                                            >
                                                <Pencil size={12} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {onRenameContract && (
                            <RenameAttachmentModal
                                show={renamingContract !== null}
                                fileName={renamingContract?.fileName ?? ''}
                                displayName={renamingContract?.displayName}
                                onHide={() => setRenamingContract(null)}
                                onConfirm={async (displayName) => {
                                    if (!renamingContract) return;
                                    await onRenameContract(renamingContract.key, displayName);
                                    setRenamingContract(null);
                                }}
                                submitting={submitting}
                            />
                        )}

                        {isAdmin && (onEdit || onEnd || onDelete) && (
                            <div className="lease-card-actions">
                                {onEnd && status !== 'ended' && (
                                    <Button variant="outline-secondary" size="sm" onClick={onEnd} disabled={submitting} title="Avsluta avtalet">
                                        <CalendarX size={14} className="me-1" />Avsluta
                                    </Button>
                                )}
                                {onEdit && (
                                    <Button variant="outline-secondary" size="sm" onClick={onEdit} disabled={submitting} title="Redigera">
                                        <Pencil size={14} />
                                    </Button>
                                )}
                                {onDelete && (
                                    <Button variant="outline-danger" size="sm" onClick={() => setConfirmDelete(true)} disabled={submitting} title="Ta bort">
                                        <Trash2 size={14} />
                                    </Button>
                                )}
                            </div>
                        )}
                    </>
                )}
        </div>
    );
};

export default LeaseCard;
